/**
 * Les effets de marque — ce que le livre dessine AUTOUR d'une couleur (Brand core V16).
 *
 * Une couleur se tient dans `colors.ts`. Ce fichier tient le reste de la recette : la géométrie,
 * les opacités et les arrêts, que le livre chiffre en CSS (`tokens.json` du kit) et qu'un
 * composant natif doit rejouer à la main, faute de `radial-gradient`.
 *
 * Volontairement hors du thème : ce sont des nombres, identiques dans tous les univers. Sur le
 * web, Unistyles ne recalcule pas une feuille quand seul un NOMBRE du thème change (il suppose
 * que tout ce qui varie est une variable CSS). Sur la nuit, un effet dont la COULEUR change la
 * lit dans le thème (l'aurore) ; un effet dont la FORME change (le surligneur) ou qui disparaît
 * (la lueur, qui n'est alors pas montée) le demande à `useIsDarkTheme` — jamais à une couleur
 * transparente, que `react-native-svg` natif ne sait pas éteindre.
 */

/**
 * Le dégradé des champions (chap. 06, « Les temps forts ») : le jaune, puis les nœuds de Zoé,
 * Tina, Sam et Abou, à 120°, arrêts 0 · 26 · 52 · 76 · 100 %. Deux dessins le portent — le cadre
 * des temps forts (`<HighlightFrame>`, couleurs pleines) et l'aurore (`<AuroraFill>`, les mêmes
 * en calque) : « les mêmes couleurs font l'aurore des champions, là où un champion a agi ».
 */
export const championRamp = {
    /** Angle CSS (0° = vers le haut, sens horaire). */
    angle: 120,
    stops: [0, 0.26, 0.52, 0.76, 1],
} as const

/**
 * Le cadre des temps forts (`<HighlightFrame>`, `temps-fort.bordure` du kit) : 2 px, et 1,5 px
 * sous le seuil `md` (800) — sur un téléphone, 2 px de dégradé pèsent plus lourd que la carte
 * qu'ils encadrent. Clés = points de rupture (`tokens/breakpoints`).
 */
export const highlightFrame = {
    width: { xs: 1.5, md: 2 },
} as const

/**
 * La lueur (chap. 03, « Les fonds ») : `radial-gradient(62% 48% at 100% 0%, rgba(255,224,0,.16)
 * 0%, rgba(255,224,0,.072) 38%, rgba(255,224,0,0) 72%)` sur la page. Une ellipse de 62 % de la
 * largeur sur 48 % de la hauteur, centrée sur le coin haut-droit ; le jaune y part à 16 %, passe
 * par 7,2 % au palier et s'efface avant le tiers de l'écran. Sa couleur est le jeton `glow`,
 * opaque : ces opacités passent par `stopOpacity`, seule voie que `react-native-svg` natif lit.
 */
export const glow = {
    /** Demi-axes de l'ellipse, en fraction de la boîte peinte. */
    radiusX: 0.62,
    radiusY: 0.48,
    /** Les trois arrêts, le long du rayon : le coin, le palier, l'effacement. */
    stops: [0, 0.38, 0.72],
    /** L'opacité du jaune à chacun de ces arrêts. */
    alphas: [0.16, 0.072, 0],
} as const

/**
 * La géométrie d'une bande de surligneur. Les longueurs sont en **em** de la taille du mot ;
 * `top` en pourcentage (une chaîne) se lit sur la hauteur de la boîte du mot, comme en CSS.
 */
export type HighlighterBand = {
    /** Retrait horizontal du mot, de chaque côté. */
    padX: number
    /** Débord de la bande au-delà de ce retrait (négatif : elle dépasse). */
    insetX: number
    /** Haut de la bande. */
    top: `${number}%` | number
    /** Bas de la bande (négatif : sous la boîte du mot). */
    bottom: number
}

/**
 * Le surligneur (chap. 03 « Le fluo passe sur l'encre », chap. 06 « Les temps forts ») — la
 * recette `.hl` du kit. Sur fond clair, une bande jaune sous le BAS du mot ; sur la nuit, elle
 * couvre le mot entier et le texte passe en marine (`.hl.nuit`). Rayon et inclinaison ne
 * changent pas d'un fond à l'autre.
 */
export const highlighter = {
    day: { padX: 0.12, insetX: -0.1, top: "56%", bottom: -0.06 },
    night: { padX: 0.14, insetX: -0.04, top: 0.04, bottom: -0.02 },
    /** Rayon de la bande, en em. */
    radius: 0.1,
    /** Inclinaison de la bande, en degrés. */
    rotate: -1.2,
} as const satisfies { day: HighlighterBand; night: HighlighterBand; radius: number; rotate: number }

/**
 * L'estompe (28/09) : l'opacité d'une IMAGE — un visage, un picto de matière — sur une séance où
 * l'enfant ne sera pas. Le texte à côté passe à l'encre secondaire (`text-muted`), jamais à une
 * opacité : l'AA vise le texte, pas l'image. Lue par `<Avatar dimmed>`, `<ImageIcon dimmed>` et
 * `<SubjectBadges dimmed>`.
 */
export const dimming = {
    opacity: 0.45,
} as const
